import {Field, HStack, Input, VStack, Switch, Separator, Text, InputGroup} from "@chakra-ui/react";
import {useEffect, useMemo, useState} from "react";
import {HiOutlineTrash} from "react-icons/hi2";
import ConfirmationButton from "@/components/ui/foundation/ConfirmationButton";
import DataFieldsPanel from "@/pages/scenario/components/DataFieldsPanel";
import {HiCheck, HiX} from "react-icons/hi";
import ConditionalTransitionsPanel from "@/pages/scenario/components/ConditionalTransitionsPanel";
import MentionTextArea from "@/pages/scenario/components/MentionTextArea";
import EndpointParamsModal from "@/pages/scenario/components/EndpointParamsModal";
import WorkerMappingModal from "@/pages/scenario/components/WorkerMappingModal";
import useEndpointVariables from "@/components/hooks/useEndpointVariables";
import useWorkerVariables from "@/components/hooks/useWorkerVariables";
import {normalizeMentionResponse} from "@/services/utilities/Mapper";
import MentionField from "@/pages/scenario/components/MentionField";
import {ScoringConfigPanel, createEmptyScoringConfig, OVERRIDE_MODE, SCORING_ENABLED} from "@/scoringUi";
import uuid4 from "uuid4";


const CreateEntityCard = ({
                              entity,
                              onRemoveClick,
                              onChange,
                              canRemove = true,
                              intents = [],
                              entities = [],
                              currentEntityIndex
                          }) => {
    const [examples, setExamples] = useState([...entity.examples]);
    const [skipPhrases, setSkipPhrases] = useState(entity.skipPhrases?.length > 0 ? [...entity.skipPhrases] : []);
    const [transitions, setTransitions] = useState(entity.transitions?.length > 0 ? [...entity.transitions] : []);
    const [item, setItem] = useState(() => {
        return {
            ...entity,
            name: entity.name?.startsWith('@') ? entity.name.slice(1) : (entity.name ?? ''),
            completeResponse: normalizeMentionResponse(entity.completeResponse ?? ""),
            response: normalizeMentionResponse(entity.response ?? ""),
        }
    });

    const entitiesNames = useMemo(() => {
        return entities?.map((entity) => {
            return {
                id: entity.id,
                display: entity.name,
                provider: "",
            }
        })
    }, [entities]);

    const {
        endpointVariables,
        dynamicValues,
        paramModalOpen,
        selectedEndpoint,
        handleMentionClick,
        handleMentionDelete,
        handleVariableSelect,
        handleParamValuesSave,
        closeParamModal,
        getInitialParamValues,
        getInitialBodySchemaValues,
    } = useEndpointVariables(entity.dynamicValues ?? [])

    const {
        workerVariables,
        dynamicWorkerValues,
        workerModalOpen,
        selectedWorker,
        handleWorkerMentionClick,
        handleWorkerMentionDelete,
        handleWorkerVariableSelect,
        handleWorkerMappingSave,
        closeWorkerModal,
        getInitialWorkerMappings,
    } = useWorkerVariables(entity.dynamicWorkerValues ?? [])
    
    const mentionSets = [
        {
            wakeUpChar: '#',
            variables: endpointVariables,
            wrapMention: true,
            color: 'blue.300',
            bg: 'rgba(66,153,225,0.15)',
            onMentionClick: handleMentionClick,
            onVariableSelect: handleVariableSelect,
            onMentionDelete: handleMentionDelete,
        },
        {
            wakeUpChar: '$',
            variables: workerVariables,
            wrapMention: true,
            color: 'green.300',
            bg: 'rgba(72,187,120,0.15)',
            onMentionClick: handleWorkerMentionClick,
            onVariableSelect: handleWorkerVariableSelect,
            onMentionDelete: handleWorkerMentionDelete,
        },
        {
            wakeUpChar: '@',
            variables: entitiesNames,
            wrapMention: true,
            color: 'purple.300',
            bg: 'rgba(72,187,120,0.15)',
            onMentionClick: () => {},
            onVariableSelect: () => {},
            onMentionDelete: () => {},
        }
    ]

    useEffect(() => {
        const apiItem = {...item, name: item.name ? `@${item.name}` : item.name};
        onChange({...apiItem, examples, skipPhrases, transitions, dynamicValues, dynamicWorkerValues});
    }, [item, examples, skipPhrases, transitions, dynamicValues, dynamicWorkerValues]);

    const handleFieldChange = (fieldName, value) => {
        if (fieldName === 'name') {
            value = value.startsWith('@') ? value.slice(1) : value;
        }
        setItem(prev => ({...prev, [fieldName]: value}));
    }

    const handleIsRequired = (value) => {
        setItem(prev => ({...prev, isRequired: value}));
    }

    const handleIsConditional = (value) => {
        setItem(prev => ({...prev, isConditional: value}));
    }

    const handleModifyResponses = (value) => {
        setItem(prev => ({...prev, modifyResponses: value}));
    }

    const handleIsFinishing = (value) => {
        setItem(prev => ({...prev, isFinishing: value}));
    }

    const handleEndConversation = (value) => {
        setItem(prev => ({...prev, endConversation: value}));
    }

    const scoring = item.scoring ?? {...createEmptyScoringConfig(), criterionKey: uuid4()};

    const handleScoringChange = (nextScoring) => {
        setItem(prev => ({...prev, scoring: nextScoring}));
    }

    const handleOverrideModeChange = (isLocked) => {
        setItem(prev => ({
            ...prev,
            scoring: {...(prev.scoring ?? createEmptyScoringConfig()), overrideMode: isLocked ? OVERRIDE_MODE.LOCKED : OVERRIDE_MODE.ALLOW_RECRUITMENT_OVERRIDE},
        }));
    }

    return (
        <VStack px={6} pb={6} borderColor={'border.secondary'} w={'full'} borderRadius={12}
                alignItems={'flex-start'}
                bg={'bg.card'} gap={6}>
            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>
            <Field.Root required>
                <Field.Label color={'white'} fontSize={18}>
                    Entity name <Field.RequiredIndicator/>
                </Field.Label>
                <InputGroup
                    startElement="@"
                    startElementProps={{color: "fg.muted", fontSize: 20}}
                >
                    <Input variant={'primary'} placeholder="e.g. userId" value={item.name}
                           onChange={(e) => handleFieldChange("name", e.target.value)}/>
                </InputGroup>
            </Field.Root>

            <Field.Root>
                <Field.Label color={'white'} fontSize={18}>
                    Display name
                </Field.Label>
                <Field.HelperText>Optional human-readable label shown in the interface.</Field.HelperText>
                <Input variant={'primary'} placeholder="e.g. User ID" value={item.displayName ?? ""}
                       onChange={(e) => handleFieldChange("displayName", e.target.value)}/>
            </Field.Root>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <HStack w={'full'}>
                <Field.Root required>
                    <Field.Label color={'white'} fontSize={18}>
                        Required entity
                    </Field.Label>
                    <Field.HelperText>
                        If enabled, the user must provide this value before the flow continues.
                    </Field.HelperText>
                </Field.Root>

                <Switch.Root checked={entity.isRequired} onCheckedChange={(e) => handleIsRequired(e.checked)}
                             colorPalette={'green'}>
                    <Switch.HiddenInput/>
                    <Switch.Control>
                        <Switch.Thumb>
                            <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                <HiCheck/>
                            </Switch.ThumbIndicator>
                        </Switch.Thumb>
                    </Switch.Control>
                    <Switch.Label/>
                </Switch.Root>
            </HStack>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <HStack w={'full'} gap={6} alignItems={'flex-start'}>
                <HStack flex={1}>
                    <Field.Root>
                        <Field.Label color={'white'} fontSize={18}>
                            Finishing entity
                        </Field.Label>
                        <Field.HelperText>
                            When this option is enabled, the bot knows that it can mark the conversation as closed after collecting this entity.
                        </Field.HelperText>
                    </Field.Root>

                    <Switch.Root checked={entity.isFinishing} onCheckedChange={(e) => handleIsFinishing(e.checked)}
                                 colorPalette={'green'}>
                        <Switch.HiddenInput/>
                        <Switch.Control>
                            <Switch.Thumb>
                                <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                    <HiCheck/>
                                </Switch.ThumbIndicator>
                            </Switch.Thumb>
                        </Switch.Control>
                        <Switch.Label/>
                    </Switch.Root>
                </HStack>

                <HStack flex={1}>
                    <Field.Root>
                        <Field.Label color={'white'} fontSize={18}>
                            End conversation
                        </Field.Label>
                        <Field.HelperText>
                            If enabled, the conversation ends after this entity is filled.
                        </Field.HelperText>
                    </Field.Root>

                    <Switch.Root checked={entity.endConversation} onCheckedChange={(e) => handleEndConversation(e.checked)}
                                 colorPalette={'green'}>
                        <Switch.HiddenInput/>
                        <Switch.Control>
                            <Switch.Thumb>
                                <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                    <HiCheck/>
                                </Switch.ThumbIndicator>
                            </Switch.Thumb>
                        </Switch.Control>
                        <Switch.Label/>
                    </Switch.Root>
                </HStack>
            </HStack>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <HStack w={'full'}>
                <Field.Root>
                    <Field.Label color={'white'} fontSize={18}>
                        Modify responses
                    </Field.Label>
                    <Field.HelperText>
                        When disabled, the response is returned exactly as entered. When enabled, it is lightly restyled each time while keeping the same meaning.
                    </Field.HelperText>
                </Field.Root>

                <Switch.Root checked={entity.modifyResponses} onCheckedChange={(e) => handleModifyResponses(e.checked)}
                             colorPalette={'green'}>
                    <Switch.HiddenInput/>
                    <Switch.Control>
                        <Switch.Thumb>
                            <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                <HiCheck/>
                            </Switch.ThumbIndicator>
                        </Switch.Thumb>
                    </Switch.Control>
                    <Switch.Label/>
                </Switch.Root>
            </HStack>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <DataFieldsPanel
                values={[...examples]}
                label={"Expected values"}
                description={"Describe what values are valid (e.g. number from 1 to 10, email, age > 18)"}
                fieldPlaceholder={'Type expected value prompt'}
                buttonLabel={'Add expected value'}
                onChange={setExamples}
            />

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <Field.Root required>
                <Field.Label color={'white'} fontSize={18}>
                    Response when valid <Field.RequiredIndicator/>
                </Field.Label>
                <Field.HelperText>Message sent when the value meets the condition.</Field.HelperText>
                <MentionTextArea
                    contentContainerProps={{flex: 2}}
                    onChange={(value) => handleFieldChange('completeResponse', value)}
                    value={item.completeResponse ?? ""}
                    mentionSets={mentionSets}
                    variant={'dark'}
                    placeholder={'Input response when valid'}
                />
            </Field.Root>

            <Field.Root required>
                <Field.Label color={'white'} fontSize={18}>
                    Response when invalid <Field.RequiredIndicator/>
                </Field.Label>
                <Field.HelperText>Message sent when the value does not meet the condition.</Field.HelperText>
                <MentionField value={item.response} onChange={(value) => handleFieldChange('response', value)} mentionSets={[
                    {
                        wakeUpChar: '#',
                        variables: endpointVariables,
                        wrapMention: true,
                        color: 'blue.300',
                        bg: 'rgba(66,153,225,0.15)',
                        onMentionClick: handleMentionClick,
                        onVariableSelect: handleVariableSelect,
                        onMentionDelete: handleMentionDelete,
                    },
                    {
                        wakeUpChar: '$',
                        variables: workerVariables,
                        wrapMention: true,
                        color: 'green.300',
                        bg: 'rgba(72,187,120,0.15)',
                        onMentionClick: handleWorkerMentionClick,
                        onVariableSelect: handleWorkerVariableSelect,
                        onMentionDelete: handleWorkerMentionDelete,
                    },
                    {
                        wakeUpChar: '@',
                        variables: entitiesNames,
                        wrapMention: true,
                        color: 'purple.300',
                        bg: 'rgba(72,187,120,0.15)',
                        onMentionClick: null,
                        onVariableSelect: () => {},
                        onMentionDelete: () => {},
                    }
                ]}/>
                {/*<Input variant={'primary' } placeholder="Input response when invalid" value={item.response}*/}
                {/*       onChange={(e) => handleFieldChange("response", e.target.value)}/>*/}
            </Field.Root>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>
            <DataFieldsPanel
                values={[...skipPhrases]}
                label={"Skip phrases"}
                description={"Add phrases that indicate the user wants to skip this question (e.g. \u201cskip\u201d, \u201cnot sure\u201d, \u201cno\u201d, \u201cNo, Im not interested\u201d)."}
                fieldPlaceholder={'Type skip phrase'}
                buttonLabel={'Add skip phrase'}
                onChange={setSkipPhrases}
            />

            <ConditionalTransitionsPanel
                entities={entities.filter((entity, i) =>  i < currentEntityIndex)}
                intents={intents}
                conditional={entity.isConditional}
                toggleConditional={handleIsConditional}
                initialTransitions={transitions}
                onChange={setTransitions}
            />

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <Field.Root required>
                <Field.Label color={'white'} fontSize={18}>
                    Next intent <Field.RequiredIndicator/>
                </Field.Label>
                <Field.HelperText>Specify which intent the bot will go to after completing the current entity, leave
                    blank if it should go to the next one in the sequence.</Field.HelperText>
                <Input variant={'primary'} placeholder="Input next intent if needed" value={item.nextIntent}
                       onChange={(e) => handleFieldChange("nextIntent", e.target.value)}/>
            </Field.Root>

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            {SCORING_ENABLED && (
                <VStack w={'full'} alignItems={'flex-start'} gap={3}>
                    <ScoringConfigPanel config={scoring} onChange={handleScoringChange}/>

                    {scoring.scoringEnabled && (
                        <HStack w={'full'} px={1}>
                            <Field.Root>
                                <Field.Label color={'white'} fontSize={14}>
                                    Zablokuj dla klienta (LOCKED)
                                </Field.Label>
                                <Field.HelperText>
                                    Gdy włączone, klient nie może zmienić konfiguracji tego kryterium w formularzu rekrutacji -- widzi ją tylko do odczytu.
                                </Field.HelperText>
                            </Field.Root>
                            <Switch.Root
                                checked={scoring.overrideMode === OVERRIDE_MODE.LOCKED}
                                onCheckedChange={(e) => handleOverrideModeChange(e.checked)}
                                colorPalette={'green'}
                            >
                                <Switch.HiddenInput/>
                                <Switch.Control>
                                    <Switch.Thumb>
                                        <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                            <HiCheck/>
                                        </Switch.ThumbIndicator>
                                    </Switch.Thumb>
                                </Switch.Control>
                                <Switch.Label/>
                            </Switch.Root>
                        </HStack>
                    )}
                </VStack>
            )}

            <Separator variant="solid" w={'full'} borderColor={'gray.700'}/>

            <HStack w={'full'}>
                {canRemove &&
                    <ConfirmationButton
                        onConfirm={onRemoveClick}
                        variant="outlined"
                        label={"Remove entity"}
                        icon={<HiOutlineTrash/>}
                    />
                }
            </HStack>

            <EndpointParamsModal
                isOpen={paramModalOpen}
                onClose={closeParamModal}
                onSave={handleParamValuesSave}
                endpoint={selectedEndpoint}
                initialValues={getInitialParamValues()}
                initialBodyValues={getInitialBodySchemaValues()}
            />

            <WorkerMappingModal
                isOpen={workerModalOpen}
                onClose={closeWorkerModal}
                onSave={handleWorkerMappingSave}
                worker={selectedWorker}
                initialMappings={getInitialWorkerMappings()}
            />

        </VStack>
    )
}

export default CreateEntityCard;
